import React, { useCallback, useRef, useEffect, useState } from 'react';
import { post } from '../lib/api';
import { Button } from './ui/button';
import VerificationCoverageMap from './VerificationCoverageMap';

export default function AccountLocationFields({ value, onChange, disabled = false }) {
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [gpsLocation, setGpsLocation] = useState(null);
  const mounted = useRef(true);
  const generation = useRef(0);
  const change = useRef(onChange); change.current = onChange;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const capture = useCallback(() => {
    const revision = ++generation.current;
    setError(''); setGpsLocation(null); change.current(null);
    if (!navigator.geolocation) { setError('GPS is unavailable. Enable location services in a supported browser.'); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async position => {
      if (!mounted.current || revision !== generation.current) return;
      const location = { type: 'Point', coordinates: [position.coords.longitude, position.coords.latitude] };
      setGpsLocation(location);
      try {
        const result = await post('/platform-map/account-location', {
          location,
          accuracy: position.coords.accuracy, capturedAt: position.timestamp,
        });
        if (mounted.current && revision === generation.current) change.current(result);
      } catch (e) { if (mounted.current && revision === generation.current) setError(e.response?.data?.message || 'Unable to verify your current location. Try again.'); }
      finally { if (mounted.current && revision === generation.current) setBusy(false); }
    }, () => {
      if (!mounted.current || revision !== generation.current) return;
      setBusy(false); setError('Enable GPS and allow browser location access to verify your account.');
    }, { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 });
  }, []);
  useEffect(() => { capture(); }, [capture]);
  return <div className="space-y-2">
    <VerificationCoverageMap location={gpsLocation} />
    <label className="block text-sm font-medium">User address
      <textarea readOnly required value={value?.completeAddress || ''} placeholder="Enable GPS to get your current account address" className="mt-2 w-full rounded-xl border bg-slate-50 p-3" />
    </label>
    <p className="text-xs text-slate-500">Your current GPS location must be inside the platform verification radius. This address is for your account; delivery addresses are managed separately.</p>
    <Button type="button" variant="outline" disabled={disabled || busy} onClick={capture}>{busy ? 'Checking current location...' : 'Refresh GPS location'}</Button>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
  </div>;
}
